// Ponte entre um formulário nosso e o do app Shopify Forms, que vive num
// DOM sombra sem ponto de estilo: o do app fica escondido e, no envio, a
// gente copia os valores para lá e clica no botão de lá, para o cadastro
// cair na lista do app. Se ele faltar, demorar ou recusar um campo, o envio
// vai para o /contact da Shopify; a tela só diz que foi quando foi.

const APP_WAIT_MS = 6000;

// Pistas de cada campo do lado do app, que traduz e renomeia rótulos.
const KEYWORDS = {
  phone: ['whatsapp', 'telefone', 'phone', 'celular'],
  email: ['email', 'e-mail', 'mail'],
  name: ['nome completo', 'full name', 'fullname', 'first name', 'firstname', 'nome'],
  company: ['razão social', 'razao social', 'nome da loja', 'nome da empresa', 'company', 'empresa'],
  cnpj: ['cnpj'],
  instagram: ['instagram', 'insta'],
  // Sem "idade", que casava com "cidade".
  tempo: ['tempo', 'quanto tempo', 'criação', 'criacao', 'fundação', 'fundacao'],
  estado: ['estado', 'state', 'uf', 'província', 'provincia'],
  cidade: ['cidade', 'city'],
  marcas: ['marcas', 'brands'],
  body: ['conte', 'sobre a sua loja', 'mensagem', 'message', 'comentário', 'comentario'],
};

// Busca que atravessa DOM sombra aberto: o app monta o formulário lá
// dentro, então um querySelector comum nunca acha.
function buscaFunda(seletor, raiz = document) {
  let achado = null;
  try {
    achado = raiz.querySelector(seletor);
  } catch {
    return null;
  }
  if (achado) return achado;
  const nos = raiz.querySelectorAll ? raiz.querySelectorAll('*') : [];
  for (const no of nos) {
    if (no.shadowRoot) {
      const dentro = buscaFunda(seletor, no.shadowRoot);
      if (dentro) return dentro;
    }
  }
  return null;
}

function acharFormularioDoApp() {
  for (const seletor of ['shopify-forms-embed', '[data-form-root="true"]', '[data-forms-id]']) {
    const host = buscaFunda(seletor);
    if (!host) continue;
    if (host.tagName === 'FORM') return host;
    const dentro = host.shadowRoot ? buscaFunda('form', host.shadowRoot) : host.querySelector('form');
    if (dentro) return dentro;
  }
  return buscaFunda('form[action*="/apps/forms"]');
}

// O app monta o formulário depois da página: espera com observador.
function esperarFormularioDoApp(prazo) {
  return new Promise((resolve) => {
    const agora = acharFormularioDoApp();
    if (agora) return resolve(agora);
    const observador = new MutationObserver(() => {
      const achado = acharFormularioDoApp();
      if (!achado) return;
      observador.disconnect();
      clearTimeout(relogio);
      resolve(achado);
    });
    observador.observe(document.body, { childList: true, subtree: true });
    const relogio = setTimeout(() => {
      observador.disconnect();
      resolve(null);
    }, prazo);
  });
}

function camposVisiveis(formulario) {
  return [...formulario.querySelectorAll('input, textarea, select')].filter((campo) => {
    const tipo = (campo.getAttribute('type') || '').toLowerCase();
    return !['hidden', 'submit', 'button', 'checkbox', 'radio'].includes(tipo);
  });
}

// Tudo o que pode identificar o campo, junto e em minúsculas: nome, id,
// exemplo, rótulo acessível e o texto do <label> ligado a ele.
function pistas(campo, formulario) {
  const partes = [
    campo.name,
    campo.id,
    campo.placeholder,
    campo.getAttribute('aria-label'),
    campo.getAttribute('autocomplete'),
  ];
  if (campo.id) {
    const rotulo = formulario.querySelector(`label[for="${CSS.escape(campo.id)}"]`);
    if (rotulo) partes.push(rotulo.textContent);
  }
  const envolve = campo.closest('label');
  if (envolve) partes.push(envolve.textContent);
  return partes.filter(Boolean).join(' ').toLowerCase();
}

function acharDestino(formulario, chave, campos) {
  const palavras = KEYWORDS[chave] || [chave];
  for (const campo of campos) {
    const pista = pistas(campo, formulario);
    if (palavras.some((p) => pista.includes(p))) return campo;
  }
  if (chave === 'email') return formulario.querySelector('input[type="email"]');
  return null;
}

// .value direto não avisa o React do app: setter do protótipo e eventos.
function escrever(campo, valor) {
  const proto = campo.tagName === 'TEXTAREA'
    ? HTMLTextAreaElement.prototype
    : campo.tagName === 'SELECT'
      ? HTMLSelectElement.prototype
      : HTMLInputElement.prototype;
  const descritor = Object.getOwnPropertyDescriptor(proto, 'value');
  if (descritor?.set) descritor.set.call(campo, valor);
  else campo.value = valor;
  campo.dispatchEvent(new Event('input', { bubbles: true }));
  campo.dispatchEvent(new Event('change', { bubbles: true }));
}

function enviarNoApp(formulario) {
  const botao = formulario.querySelector('button[type="submit"], input[type="submit"]');
  if (botao) return botao.click();
  if (typeof formulario.requestSubmit === 'function') return formulario.requestSubmit();
  formulario.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}

// Clicar não é entregar: vale a resposta do envio do app, lida na entrada de
// rede (um app de carrinho troca o fetch da página, então embrulhá-lo falha).
const ENVIO_DO_APP = 'forms.shopifyapps.com/api/v2/form_submission';
let envioNaRede = null;
if ('PerformanceObserver' in window) {
  new PerformanceObserver((lista) => {
    for (const entrada of lista.getEntries()) {
      if (entrada.name.includes(ENVIO_DO_APP)) envioNaRede = entrada;
    }
  }).observe({ type: 'resource' });
}

function desafioAberto() {
  return [...document.querySelectorAll('iframe[src*="hcaptcha"]')].some((quadro) => {
    if (!quadro.src.includes('frame=challenge')) return false;
    return getComputedStyle(quadro).visibility === 'visible' && quadro.getBoundingClientRect().width > 0;
  });
}

// Até 3 min com o desafio do hCaptcha aberto; sem ele e sem resposta, 20s.
function aceitoPeloApp(formulario) {
  return new Promise((resolve) => {
    const botao = formulario.querySelector('button[type="submit"], input[type="submit"]');
    let botaoTravou = false;
    let botaoVoltou = 0;
    let ultimoSinal = Date.now();
    const fim = (entregue) => {
      clearInterval(relogio);
      clearTimeout(limite);
      resolve(entregue);
    };
    const limite = setTimeout(() => fim(false), 180000);
    const relogio = setInterval(() => {
      if (envioNaRede) {
        const status = envioNaRede.responseStatus;
        return fim(status ? status >= 200 && status < 300 : !formulario.querySelector('[aria-invalid="true"]'));
      }
      if (formulario.querySelector('[aria-invalid="true"]')) return fim(false);
      if (botao?.disabled) {
        botaoTravou = true;
        botaoVoltou = 0;
      } else if (botaoTravou && !botaoVoltou) {
        botaoVoltou = Date.now();
      }
      // O botão volta também depois de envio certo; a rede chega logo depois.
      if (botaoVoltou && Date.now() - botaoVoltou > 1500) return fim(false);
      if (desafioAberto()) ultimoSinal = Date.now();
      else if (Date.now() - ultimoSinal > 20000) fim(false);
    }, 250);
  });
}

// Queda: o /contact da Shopify manda para o e-mail da loja; entregue é a
// volta com contact_posted=true. Com o hCaptcha da loja ligado, ele recusa.
async function enviarNoContact(nosso) {
  const dados = new FormData();
  dados.append('form_type', 'contact');
  dados.append('utf8', '✓');
  for (const campo of nosso.elements) {
    if (campo.name && campo.value) dados.append(campo.name, campo.value);
  }
  const resposta = await fetch(`${window.Shopify?.routes?.root || '/'}contact`, {
    method: 'POST',
    body: dados,
    credentials: 'same-origin',
  });
  return resposta.url.includes('contact_posted=true');
}

function avisar(nosso, estado, mensagem) {
  const alvo = nosso.querySelector('[data-lojista-status]');
  if (!alvo) return;
  alvo.dataset.state = estado;
  alvo.textContent = mensagem;
}

function ligar(nosso) {
  if (nosso.dataset.ligado) return;
  nosso.dataset.ligado = '1';

  nosso.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    // A validação do navegador continua valendo: os campos obrigatórios
    // são os nossos, não os do app.
    if (!nosso.checkValidity()) return nosso.reportValidity();

    const botao = nosso.querySelector('[type="submit"]');
    if (botao?.disabled) return;
    if (botao) botao.disabled = true;
    avisar(nosso, 'enviando', nosso.dataset.sending || '');

    const nossos = [...nosso.elements].filter((campo) => campo.name && campo.value);
    let entregue = false;

    const doApp = await esperarFormularioDoApp(APP_WAIT_MS);
    if (doApp) {
      const destinos = camposVisiveis(doApp);
      let preenchidos = 0;
      for (const campo of nossos) {
        const chave = (campo.name.match(/\[(.+)\]/) || [])[1];
        if (!chave) continue;
        const destino = acharDestino(doApp, chave, destinos);
        if (!destino) continue;
        // O telefone do app tem o +55 fixo e recusa o valor sem ele.
        const valor = chave === 'phone' && !campo.value.trim().startsWith('+') ? `+55 ${campo.value}` : campo.value;
        escrever(destino, valor);
        preenchidos += 1;
      }
      if (preenchidos > 0) {
        envioNaRede = null;
        enviarNoApp(doApp);
        entregue = await aceitoPeloApp(doApp);
      }
    }

    if (!entregue) {
      try {
        entregue = await enviarNoContact(nosso);
      } catch {
        entregue = false;
      }
    }

    if (entregue) {
      avisar(nosso, 'ok', nosso.dataset.success || '');
      nosso.reset();
    } else {
      avisar(nosso, 'erro', nosso.dataset.error || '');
    }
    if (botao) setTimeout(() => { botao.disabled = false; }, 1200);
  });
}

document.querySelectorAll('[data-lojista-form]').forEach(ligar);
document.addEventListener('shopify:section:load', (evento) => {
  evento.target.querySelectorAll('[data-lojista-form]').forEach(ligar);
});
